import { ROUTE_TITLES } from "../Layout/menu-items";

export interface BreadcrumbItemData {
  label: string;
  /** Ancestors carry an href; the current page never does (antd convention). */
  href?: string;
}

// Child-route labels per section. Only sections with detail/creation routes
// need an entry; the generic fallbacks below cover the rest. Labels carry
// the entity noun (and its gender) because "Editar" alone reads incomplete.
const CHILD_LABELS: Record<string, { create: string; edit: string }> = {
  "/users": { create: "Nuevo usuario", edit: "Editar usuario" },
  "/schools": { create: "Nueva escuela", edit: "Editar escuela" },
  "/courses": { create: "Nuevo curso", edit: "Editar curso" },
  "/lessons": { create: "Nueva lección", edit: "Editar lección" },
  "/subjects": { create: "Nueva asignatura", edit: "Editar asignatura" },
  "/resources": { create: "Nuevo recurso", edit: "Editar recurso" },
  "/grades": { create: "Nuevo grado", edit: "Editar grado" },
  "/programs": { create: "Nuevo programa", edit: "Editar programa" },
  "/game-quizzes": { create: "Nuevo quiz", edit: "Editar quiz" },
  "/tienda-paquetes": { create: "Nuevo paquete", edit: "Editar paquete" },
};

// Projects reuse the /courses/:id and /courses/new routes with ?project=1
// (see the Courses page), so the crumb must follow the same signal.
const PROJECT_CHILD_LABELS = {
  create: "Nuevo proyecto",
  edit: "Editar proyecto",
};

const childLabel = (
  basePath: string,
  segment: string,
  search: string,
): string => {
  const isProject =
    basePath === "/courses" &&
    new URLSearchParams(search).get("project") === "1";
  const labels = isProject
    ? PROJECT_CHILD_LABELS
    : CHILD_LABELS[basePath];
  if (segment === "new") return labels?.create ?? "Nuevo";
  return labels?.edit ?? "Detalle";
};

/**
 * Derives breadcrumb items from the current location. Unknown routes and the
 * dashboard produce an empty list (the component renders nothing), so a bad
 * URL can never crash the layout.
 */
export const buildBreadcrumbItems = (
  pathname: string,
  search: string,
): BreadcrumbItemData[] => {
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return [];

  const basePath = `/${segments[0]}`;
  const sectionTitle = ROUTE_TITLES[basePath];
  if (!sectionTitle) return [];

  const items: BreadcrumbItemData[] = [{ label: "Inicio", href: "/" }];
  if (segments.length === 1) {
    items.push({ label: sectionTitle });
    return items;
  }

  items.push({ label: sectionTitle, href: basePath });
  items.push({ label: childLabel(basePath, segments[1], search) });
  return items;
};
